import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { startImpersonation, endImpersonation, getActiveImpersonation } from '../lib/db';

// Single source of truth for super-admin impersonation state.
//
// The DB is authoritative: `super_admin_impersonations` is RLS-protected and
// `current_firm_id()` reads from it, so RLS enforces tenant scope regardless
// of what the client thinks. This context exists only so the UI can render
// the banner, bypass onboarding/suspension screens, and pick the SuperAdmin
// vs. tenant app shell.
//
// We mirror the active row in sessionStorage for fast initial paint, but the
// cache is reconciled with the server on every mount.

const CACHE_KEY = 'sa_impersonation_cache_v1';

const ImpersonationContext = createContext({
  loading: true,
  impersonating: null, // null | { firm_id, firm_name, expires_at }
  start: async () => {},
  end:   async () => {},
});

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.expires_at && new Date(parsed.expires_at) <= new Date()) return null;
    return parsed;
  } catch { return null; }
}

function writeCache(value) {
  try {
    if (value) sessionStorage.setItem(CACHE_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(CACHE_KEY);
  } catch { /* ignore */ }
}

export function ImpersonationProvider({ children }) {
  const { user, userProfile } = useAuth();
  const [impersonating, setImpersonating] = useState(() => readCache());
  const [loading, setLoading] = useState(true);

  // Reconcile with server whenever we have a (super-admin) session.
  useEffect(() => {
    let cancelled = false;
    async function reconcile() {
      if (!user) { setImpersonating(null); writeCache(null); setLoading(false); return; }
      if (!userProfile?.is_super_admin) {
        // Only super-admins can impersonate. Clear any stale cache.
        if (impersonating) { setImpersonating(null); writeCache(null); }
        setLoading(false);
        return;
      }
      const { data, error } = await getActiveImpersonation();
      if (cancelled) return;
      if (error) {
        // Treat any error as "no active impersonation" — fail closed.
        setImpersonating(null); writeCache(null);
      } else {
        setImpersonating(data || null);
        writeCache(data || null);
      }
      setLoading(false);
    }
    reconcile();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, userProfile?.is_super_admin]);

  const start = useCallback(async (firmId) => {
    const { data, error } = await startImpersonation(firmId);
    if (error) throw error;
    setImpersonating(data);
    writeCache(data);
    return data;
  }, []);

  const end = useCallback(async () => {
    const { error } = await endImpersonation();
    if (error) throw error;
    setImpersonating(null);
    writeCache(null);
  }, []);

  return (
    <ImpersonationContext.Provider value={{ loading, impersonating, start, end }}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export const useImpersonation = () => useContext(ImpersonationContext);
